import type { PureQueryOptions } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v3';
import type { ReviewCommentParentType } from '~/apollo/generated/v3/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { TextAreaInput } from '~/components/ui/forms/text-area-input';
import { useZodForm } from '~/utils/forms';

export const CREATE_REVIEW_COMMENT = graphql(`
  mutation CreateReviewComment(
    $parentType: ReviewCommentParentType!
    $parentId: Int!
    $comment: String!
  ) {
    createReviewComment(
      parentType: $parentType
      parentId: $parentId
      comment: $comment
    ) {
      ...reviewCommentParts
      author {
        ...publicUserParts
      }
    }
  }
`);

const schema = z.object({
  comment: z.string().min(1),
});

type Props = {
  parentType: ReviewCommentParentType;
  parentId: number;
  name: string;
  refetchQueries: PureQueryOptions[];
};

export function CreateReviewComment({
  parentType,
  parentId,
  name,
  refetchQueries,
}: Props) {
  const [createComment, { loading, error }] = useMutation(
    CREATE_REVIEW_COMMENT,
    { refetchQueries },
  );

  const form = useZodForm({
    schema,
    values: { comment: '' },
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createComment({
        variables: {
          parentType,
          parentId,
          comment: values.comment,
        },
      });
      toast.success('Your comment has been added successfully.');
      form.reset({ comment: '' });
    } catch (err) {
      console.log('Error creating review comment', err);
      toast.error('There was a problem saving your review comment.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex-grow">
            <TextAreaInput
              {...form.register('comment')}
              placeholder={`Add a comment for ${name}`}
              required
            />
          </div>

          <Button type="submit" color="primary" size="sm" loading={loading}>
            Add Comment
          </Button>
        </div>

        <HookFormErrors graphQLError={error} />
      </form>
    </FormProvider>
  );
}
